<template>
  <v-navigation-drawer
    v-model="sidebarVisible"
    app
    :width="canSeeModules ? 330 : 270"
  >
    <v-row
      no-gutters
      style="min-height: 100%"
    >
      <v-col
        v-if="canSeeModules"
        cols="3"
        class="module-navigation grey lighten-5"
      >
        <module-btn
          module-name="business"
          :active="currentModule === 'business'"
          icon="business_center"
        />
        <module-btn
          module-name="settings"
          :active="currentModule === 'settings'"
          icon="settings"
        />
        <module-btn
          module-name="service"
          :active="currentModule === 'service'"
          icon="build"
        />
      </v-col>
      <v-col
        :cols="canSeeModules ? 13 : 16"
        class="elevation-15"
      >
        <div class="px-12">
          <v-alert
            :value="!!inventoryItemsIssued.data.length"
            color="warning"
            icon="priority_high"
            outlined
          >
            {{ inventoryItemsIssued.data.length }} issued items
          </v-alert>
        </div>
        <!-- sidebar header -->
        <v-subheader class="sidebar-header justify-center subtitle-1 font-weight-light">
          Navigare CYMS
        </v-subheader><!-- end sidebar header -->

        <v-divider />

        <div v-if="currentModule === 'service'">
          <current-location-select />
        </div>

        <v-list
          class="pa-0"
          dense
          flat
        >
          <template v-for="item in items[currentModule]">
            <template v-if="item.children">
              <div
                v-if="item.children.filter(c => c.visible()).length"
                :key="item.label"
                class="grey--text text--lighten-1 px-4 mt-6 mb-1 caption text-uppercase font-weight-bold"
              >
                {{ item.label }}
              </div>

              <v-list-item
                v-for="subItem in item.children.filter(c => c.visible())"
                :key="subItem.label"
                :to="{ name: subItem.link }"
                exact
              >
                <v-list-item-icon class="mr-4">
                  <v-icon
                    small
                    color="grey"
                  >
                    {{ subItem.icon }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="grey--text text--darken-3 font-weight-regular">
                    {{ subItem.label }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>

            <v-list-item
              v-else-if="item.visible()"
              :key="item.label"
              :to="{ name: item.link }"
              exact
            >
              <v-list-item-icon class="mr-4">
                <v-icon
                  small
                  color="grey"
                >
                  {{ item.icon }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text text--darken-3 font-weight-regular">
                  {{ item.label }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-if="currentModule === 'business'">
            <v-divider />
            <v-list-item
              v-if="$can('browse-fleet-financials')"
              href="https://sites.google.com/navigare-yachting.com/fleetfinancials/home"
              target="_blank"
            >
              <v-list-item-icon class="mr-4">
                <v-icon
                  small
                  color="primary"
                >
                  fa fa-question-circle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="primary--text font-weight-regular">
                  Fleet Financials
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="$can('browse-yard-availability')"
              href="https://sites.google.com/navigare-yachting.com/availability/home"
              target="_blank"
            >
              <v-list-item-icon class="mr-4">
                <v-icon
                  small
                  color="primary"
                >
                  fa fa-question-circle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="primary--text font-weight-regular">
                  Yacht Sales Guide
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
          <template v-if="currentModule === 'service'">
            <v-divider />
            <v-list-item
              href="https://sites.google.com/navigare-yachting.com/spareparts-warranty/home"
              target="_blank"
            >
              <v-list-item-icon class="mr-4">
                <v-icon
                  small
                  color="primary"
                >
                  fa fa-question-circle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="primary--text font-weight-regular">
                  Operations Guide
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list>
      </v-col>
    </v-row>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from 'vuex'
import ModuleBtn from '@/components/navigation/ModuleBtn'
import CurrentLocationSelect from '@/components/service/helpers/CurrentLocationSelect'

export default {
    name: 'Sidebar',
    components: {
      ModuleBtn,
      CurrentLocationSelect
    },
    props: {
      'drawer': Boolean
    },
    data() {
      return {
        items: {
          settings: [
            {
              label: 'Yachts',
              children: [
                {
                  icon: 'directions_boat',
                  label: 'Models',
                  link: 'yachtModels',
                  visible: () => this.$can('browse', 'YachtModel')
                },
                {
                  icon: 'view_quilt',
                  label: 'Equipment Packs',
                  link: 'yachtEquipmentPacks',
                  visible: () => this.$can('browse', 'YachtEquipmentPack')
                },
                {
                  icon: 'style',
                  label: 'Yacht Types',
                  link: 'yachtTypes',
                  visible: () => this.$can('browse', 'YachtType')
                },
                {
                  icon: 'toys',
                  label: 'Yacht Engines',
                  link: 'yachtEngines',
                  visible: () => this.$can('browse', 'YachtEngine')
                },
                {
                  icon: 'location_city',
                  label: 'Yacht Manufactures',
                  link: 'yachtManufactures',
                  visible: () => this.$can('browse', 'YachtManufacturer')
                },
                {
                  icon: 'view_quilt',
                  label: 'Equipment Pack Categories',
                  link: 'yachtEquipmentPackCategories',
                  visible: () => this.$can('browse', 'YachtEquipmentPackCategory')
                }
              ]
            },
            {
              label: 'Financial',
              children: [
                {
                  icon: 'assessment',
                  label: 'Yacht Incomes & Costs',
                  link: 'yachtFinancials',
                  visible: () => this.$can('set-income-costs', 'YachtModel')
                },
                {
                  icon: 'assessment',
                  label: 'Operational Cost Control',
                  link: 'costControl',
                  visible: () => this.$can('set-income-costs', 'YachtModel')
                },
                {
                  icon: 'show_chart',
                  label: 'Investment Programs',
                  link: 'financeModels',
                  visible: () => this.$can('browse', 'FinanceModel')
                },
                {
                  icon: 'attach_money',
                  label: 'Currency Exchange Rates',
                  link: 'currencyExchangeRates',
                  visible: () => this.$can('browse', 'CurrencyExchangeRate')
                },
                {
                  icon: 'attach_money',
                  label: 'Prices & Margins',
                  link: 'margins',
                  visible: () => this.$can('browse-margins')
                },
                {
                  icon: 'attach_money',
                  label: 'Yacht Booking Pricelist',
                  link: 'bookingPricelist',
                  visible: () => this.$can('browse-booking-pricelist')
                }
              ]
            },
            {
              label: 'Documents',
              children: [
                {
                  icon: 'library_books',
                  label: 'Document Templates',
                  link: 'documentTemplates',
                  visible: () => this.$can('browse', 'DocumentTemplate')
                }
              ]
            },
            {
              label: 'Navigare',
              children: [
                {
                  icon: 'public',
                  label: 'Countries',
                  link: 'countries',
                  visible: () => this.$can('browse', 'Country')
                },
                {
                  icon: 'waves',
                  label: 'Bases',
                  link: 'bases',
                  visible: () => this.$can('browse', 'Base')
                },
                {
                  icon: 'group',
                  label: 'Staff',
                  link: 'staff',
                  visible: () => this.$can('browse', 'Staff')
                },
                {
                  icon: 'business',
                  label: 'Navigare Companies',
                  link: 'navigareCompanies',
                  visible: () => this.$can('browse', 'NavigareCompany')
                },
                {
                  icon: 'local_shipping',
                  label: 'Contractors',
                  link: 'contractors',
                  visible: () => this.$can('browse', 'Contractor')
                },
                {
                  icon: 'settings_input_svideo',
                  label: 'Roles',
                  link: 'roles',
                  visible: () => this.$can('browse', 'roles')
                }
              ]
            },
            {
              label: 'Service',
              children: [
                {
                  icon: 'fa fa-database',
                  label: 'Service Tasks Repository',
                  link: 'serviceTasksRepository',
                  visible: () => this.$can('browse', 'ServiceTasksRepository')
                }
              ]
            }
          ],
          business: [
            {
              icon: 'directions_boat',
              label: 'Yachts',
              link: 'yachts',
              visible: () => this.$can('browse', 'Yacht')
            },
            {
              icon: 'storefront',
              label: 'Yacht Sales',
              link: 'yachtSales',
              visible: () => this.$can('browse-sales', 'Yacht')
            },
            {
              icon: 'list_alt',
              label: 'Yacht Models / Pricelist',
              link: 'yachtModelsPricelist',
              visible: () => this.$can('browse-sales', 'YachtModel')
            },
            {
              icon: 'group',
              label: 'Yacht owners',
              link: 'owners',
              visible: () => this.$can('browse', 'Owner')
            },
            {
              icon: 'fa fa-shipping-fast',
              label: 'Yacht Logistics',
              link: 'logistics',
              visible: () => this.$can('browse-logistics', 'Yacht')
            },
            {
              icon: 'fa fa-shipping-fast',
              label: 'Equipment Logistics',
              link: 'equipmentLogistics',
              visible: () => this.$can('browse-equipment-logistics', 'Yacht')
            }

          ],
          service: [
            {
              icon: 'fa fa-wrench',
              label: 'Tasks',
              link: 'serviceTasks',
              visible: () => this.$can('browse', 'ServiceTask')
            },
            {
              icon: 'directions_boat',
              label: 'Yachts',
              link: 'serviceYachts',
              visible: () => this.$can('browse', 'Yacht')
            },
            {
              icon: 'fa fa-th-large',
              label: 'Task Types',
              link: 'serviceTaskTypes',
              visible: () => this.$can('browse', 'ServiceTaskType')
            },
            {
              icon: 'fa fa-money-check-alt',
              label: 'Service Cost Reports',
              link: 'serviceCostReports',
              visible: () => this.$can('browse', 'ServiceCostReport')
            },
            {
              icon: 'fa fa-warehouse',
              label: 'Inventory',
              link: 'inventoryItems',
              visible: () => this.$can('browse', 'InventoryItem')
            },
            {
              icon: 'fa fa-tasks',
              label: 'Checklists',
              link: 'checklists',
              visible: () => this.$can('browse', 'Checklist')
            },
            {
              icon: 'fa fa-chart-bar',
              label: 'Stats',
              link: 'serviceStats',
              visible: () => this.$can('browse-service-stats')
            }
          ]
        }
      }
    },
    computed: {
      sidebarVisible: {
        get: function () {
          return this.drawer
        },
        set: function (value) {
          this.$emit('update:drawer', value)
        }
      },
      currentModule() {
        if (this.$route.path.indexOf('/settings') > -1) return 'settings'
        if (this.$route.path.indexOf('/business') > -1) return 'business'
        if (this.$route.path.indexOf('/service') > -1) return 'service'

        return null
      },
      canSeeModules() {
        let permissions = [
          this.$can('access-business'),
          this.$can('access-settings'),
          this.$can('access-service')
        ]

        return permissions.filter(p => !!p).length > 1
      },
      ...mapState('auth', ['user']),
      ...mapState('service', ['inventoryItemsIssued'])
    },
    mounted() {
      if (!this.$vuetify.breakpoint.xsOnly) this.sidebarVisible = true
    }
  }
</script>

<style lang="sass">

/*  aside.v-navigation-drawer a.v-list__tile
    text-decoration: none
    color: #fafafa !important*/

.module-navigation
  padding-top: 65px !important
  overflow: hidden

  .v-btn
    padding-top: 14px !important
    padding-bottom: 14px !important
    height: 50px !important
    border-radius: 0 !important

    &.v-btn--active:before
      background-color: #ffffff !important
      opacity: 1

.sidebar-header
  height: 48px

</style>
